import { initializeApp } from 'firebase/app'
import { getDatabase } from 'firebase/database'
import { getAnalytics } from 'firebase/analytics'
import {
  getRemoteConfig,
  getValue as getValueLibrary,
  fetchAndActivate as fetchAndActivateLibrary,
  getAll as getAllLibrary
} from 'firebase/remote-config'
import type { IFeatureFlags } from '@/models/interfaces'
import { getDefaultFeatureFlagsForInterface } from '@/config/defaultFeatureFlags'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_APP_FIREBASE_APIKEY,
  authDomain: import.meta.env.VITE_APP_FIREBASE_AUTHDOMAIN,
  databaseURL: import.meta.env.VITE_APP_FIREBASE_DATABASEURL,
  projectId: import.meta.env.VITE_APP_FIREBASE_PROJECTID,
  storageBucket: import.meta.env.VITE_APP_FIREBASE_STORAGEBUCKET,
  messagingSenderId: import.meta.env.VITE_APP_FIREBASE_SENDERID,
  appId: import.meta.env.VITE_APP_FIREBASE_APPID,
  measurementId: import.meta.env.VITE_APP_FIREBASE_MEASUREMENTID
}

// Global feature flags with default values - imported from single source of truth
const globalFeatureFlags: Partial<IFeatureFlags> =
  getDefaultFeatureFlagsForInterface() as Partial<IFeatureFlags>

class FirebaseService {
  private app!: ReturnType<typeof initializeApp>
  private _database?: ReturnType<typeof getDatabase>
  private _remoteConfig!: ReturnType<typeof getRemoteConfig>
  private _analytics!: ReturnType<typeof getAnalytics>
  private globalConfigFetched = false

  constructor() {
    this.initialize()
  }

  // ---- Initialization ----
  private initialize() {
    this.app = initializeApp(firebaseConfig)

    // Only initialize database if URL is configured
    if (firebaseConfig.databaseURL) {
      this._database = getDatabase(this.app)
    }

    this._remoteConfig = getRemoteConfig(this.app)
    this._analytics = getAnalytics(this.app)

    // Set defaults for global feature flags
    this._remoteConfig.defaultConfig = globalFeatureFlags as {
      [key: string]: string | number | boolean
    }

    // 43200000 = 12 hours (recommended prod interval)
    this._remoteConfig.settings.minimumFetchIntervalMillis = import.meta.env
      .VITE_APP_FIREBASE_INTERVAL_MS
      ? Number(import.meta.env.VITE_APP_FIREBASE_INTERVAL_MS)
      : import.meta.env.MODE === 'development'
        ? 60000 // 1 minute in dev
        : 43200000 // 12 hours in prod
  }

  // ---- Remote Config: Simplified Feature Flags ----
  /**
   * Fetches ALL Remote Config values at once - no need to predefine flags!
   * This supports the dynamic useFeatureFlag system where any flag name works.
   */
  async fetchAllRemoteConfig(): Promise<Record<string, unknown>> {
    if (!this.globalConfigFetched) {
      await fetchAndActivateLibrary(this._remoteConfig)
      this.globalConfigFetched = true
    }

    // Get ALL Remote Config values (not just predefined ones)
    const allValues = getAllLibrary(this._remoteConfig)
    const config: Record<string, unknown> = {}

    // Convert Firebase Remote Config values to JavaScript values
    Object.keys(allValues).forEach((key) => {
      const value = allValues[key]

      // Convert based on source (try to preserve types)
      if (value.getSource() === 'remote' || value.getSource() === 'default') {
        const stringValue = value.asString()

        // Try to parse as boolean
        if (stringValue === 'true') config[key] = true
        else if (stringValue === 'false') config[key] = false
        // Try to parse as number
        else if (!isNaN(Number(stringValue)) && stringValue !== '')
          config[key] = Number(stringValue)
        // Keep as string
        else config[key] = stringValue
      }
    })

    return config
  }

  /**
   * Forces a fresh fetch of all Remote Config values (bypasses cache)
   */
  async forceRefreshAllRemoteConfig(): Promise<Record<string, unknown>> {
    const originalInterval =
      this._remoteConfig.settings.minimumFetchIntervalMillis
    this._remoteConfig.settings.minimumFetchIntervalMillis = 0

    try {
      await fetchAndActivateLibrary(this._remoteConfig)
      this.globalConfigFetched = true
    } finally {
      this._remoteConfig.settings.minimumFetchIntervalMillis = originalInterval
    }

    return this.fetchAllRemoteConfig()
  }

  // ---- Utility Methods ----
  getValue(key: string) {
    return getValueLibrary(this._remoteConfig, key)
  }

  getFeatureFlagValue(key: string): boolean {
    return this.getValue(key).asBoolean()
  }

  async fetchAndActivate() {
    return await fetchAndActivateLibrary(this._remoteConfig)
  }

  getAll() {
    return getAllLibrary(this._remoteConfig)
  }

  getAllGlobalFeatureFlagValues(): Partial<IFeatureFlags> {
    const featureFlags: Partial<IFeatureFlags> = {}

    Object.keys(globalFeatureFlags).forEach((key) => {
      const typedKey = key as keyof IFeatureFlags
      featureFlags[typedKey] = this.getValue(typedKey).asBoolean()
    })

    return featureFlags
  }

  // ---- Getters (for Realtime DB + Analytics) ----
  get database(): ReturnType<typeof getDatabase> | null {
    if (!this._database) {
      console.warn(
        'Firebase Realtime Database not initialized - missing databaseURL configuration'
      )
      return null
    }
    return this._database
  }

  get remoteConfig(): ReturnType<typeof getRemoteConfig> {
    return this._remoteConfig
  }

  get analytics(): ReturnType<typeof getAnalytics> {
    return this._analytics
  }
}

// =======================
// Singleton & Exports
// =======================
const firebaseService = new FirebaseService()

// Export convenience functions for backward compatibility
const database = firebaseService.database
const remoteConfig = firebaseService.remoteConfig
const fetchAndActivate = () => firebaseService.fetchAndActivate()
const getValue = (key: string) => firebaseService.getValue(key)
const getFeatureFlagValue = (key: string) =>
  firebaseService.getFeatureFlagValue(key)
const getAll = () => firebaseService.getAll()
const getAllGlobalFeatureFlagValues = () =>
  firebaseService.getAllGlobalFeatureFlagValues()

export type { IFeatureFlags }
export {
  firebaseService,
  database, // ✅ Realtime Database (for live notifications)
  remoteConfig,
  fetchAndActivate,
  getValue,
  getFeatureFlagValue,
  getAll,
  getAllGlobalFeatureFlagValues
}
